import { chargeLineGr, chargeTotalGr, type MeDto, SERVICE_CHARGE_STATES, SERVICE_INVOICE_STATES, type ServiceChargeDto, type ServiceChargeLine, type ServicePriceListItem } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Group, Modal, NumberInput, Select, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconPlus, IconReceiptRefund, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { Money, MoneyInput } from '../../components/Money';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';

const STATE_COLORS = { brak: 'gray', do_zaplaty: 'orange', czesciowo: 'yellow', oplacone: 'green' } as const;
const INVOICE_COLORS = { brak: 'gray', wysylanie: 'blue', wystawiona: 'green', blad: 'red', niepewna: 'orange', reczna: 'green' } as const;

/**
 * docs/26: rozliczenie zgłoszenia w oknie zgłoszenia — pozycje do zapłaty (z cennika serwisu albo spoza cennika),
 * wpłata online, link „Zapłać online” e-mailem, faktura wystawiana automatycznie po wpłacie i nadpłaty do zwrotu.
 */
export function ServiceChargeBlock({ ticketId, me, warranty }: { ticketId: string; me: MeDto; warranty: boolean }) {
  const qc = useQueryClient();
  const key = ['service-charge', ticketId];
  const visible = can(me, 'serwis.platnosci') || can(me, 'faktury.podglad');
  const q = useQuery({ queryKey: key, queryFn: () => api<ServiceChargeDto>(`/service-tickets/${ticketId}/charge`), enabled: visible });
  const [edit, setEdit] = useState<ServiceChargeLine[] | null>(null);
  const [refund, setRefund] = useState<{ id: string; amountGr: number; note: string } | null>(null);
  const done = (msg: string) => (r: ServiceChargeDto) => {
    qc.setQueryData(key, r);
    void qc.invalidateQueries({ queryKey: ['service-ticket', ticketId] });
    notifyOk(msg);
  };
  const save = useMutation({ mutationFn: (items: ServiceChargeLine[]) => api<ServiceChargeDto>(`/service-tickets/${ticketId}/charge`, { method: 'PUT', body: { items } }), onSuccess: (r) => (done('Zapisano pozycje do zapłaty')(r), setEdit(null)), onError: notifyError });
  const link = useMutation({ mutationFn: () => api<ServiceChargeDto>(`/service-tickets/${ticketId}/charge/link`, { method: 'POST' }), onSuccess: (r) => done(`Wysłano link do zapłaty na ${r.linkSentTo ?? 'adres klienta'}`)(r), onError: notifyError });
  const invoice = useMutation({ mutationFn: () => api<ServiceChargeDto>(`/service-tickets/${ticketId}/charge/invoice`, { method: 'POST' }), onSuccess: (r) => done(r.invoice.state === 'wystawiona' ? `Wystawiono fakturę ${r.invoice.number ?? ''}`.trim() : 'Ponowiono wystawienie faktury')(r), onError: notifyError });
  const resolve = useMutation({
    mutationFn: (x: { id: string; note: string }) => api<ServiceChargeDto>(`/service-tickets/${ticketId}/charge/overpayments/${x.id}/resolve`, { method: 'POST', body: { note: x.note } }),
    onSuccess: (r) => (done('Zwrot oznaczony')(r), setRefund(null)),
    onError: notifyError,
  });
  if (!visible || !q.data) return null;
  const d = q.data;
  const office = d.items.filter((i) => i.source === 'biuro');
  return (
    <Stack gap={6}>
      <Group gap={6} wrap="wrap">
        <Badge size="sm" color={STATE_COLORS[d.state]}>
          {SERVICE_CHARGE_STATES[d.state]}
        </Badge>
        {d.totalGr > 0 && (
          <Text size="sm">
            razem <Money gr={d.totalGr} /> brutto{d.paidGr > 0 && <> · zapłacono <Money gr={d.paidGr} /></>}
            {d.toPayGr > 0 && d.paidGr > 0 && <> · pozostało <Money gr={d.toPayGr} /></>}
          </Text>
        )}
        {d.totalGr > 0 && (
          <Badge size="sm" variant="light" color={INVOICE_COLORS[d.invoice.state]}>
            {d.invoice.state === 'wystawiona' && d.invoice.number ? `Faktura ${d.invoice.number}` : SERVICE_INVOICE_STATES[d.invoice.state]}
          </Badge>
        )}
        {d.can.edit && (
          <Anchor component="button" size="xs" onClick={() => setEdit(office.map(({ catalogItemId, name, unit, quantity, unitGrossGr, vatRate, free }) => ({ catalogItemId, name, unit, quantity, unitGrossGr, vatRate, free })))}>
            {office.length ? 'Zmień pozycje biura' : 'Dodaj pozycje do zapłaty'}
          </Anchor>
        )}
        {d.can.sendLink && (
          <Anchor component="button" size="xs" onClick={() => link.mutate()} disabled={link.isPending}>
            {d.linkSentAt ? 'Wyślij link ponownie' : 'Wyślij link do zapłaty'}
          </Anchor>
        )}
        {d.can.invoice && (
          <Anchor component="button" size="xs" onClick={() => invoice.mutate()} disabled={invoice.isPending}>
            Ponów wystawienie faktury
          </Anchor>
        )}
      </Group>
      {d.items.length > 0 && (
        <Table withRowBorders={false} verticalSpacing={2} fz="sm">
          <Table.Tbody>
            {d.items.map((i) => (
              <Table.Tr key={i.id}>
                <Table.Td>
                  {i.name}
                  {i.offList && !i.free && (
                    <Badge size="xs" variant="light" color="orange" ml={6}>
                      Spoza cennika
                    </Badge>
                  )}
                  {i.priceDiffers && (
                    <Badge size="xs" variant="light" color="yellow" ml={6}>
                      Cena inna niż w cenniku
                    </Badge>
                  )}
                  <Text span size="xs" c="dimmed">
                    {' '}
                    · {i.source === 'teren' ? `z protokołu${i.protocolCode ? ` ${i.protocolCode}` : ''}` : 'biuro'}
                    {i.addedByName ? ` · ${i.addedByName}` : ''}
                  </Text>
                </Table.Td>
                <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                  {String(i.quantity).replace('.', ',')} {i.unit}
                </Table.Td>
                <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
                  {i.free ? <Text size="xs" c="dimmed">0 zł · w ramach gwarancji</Text> : <Money gr={chargeLineGr(i)} />}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {d.state === 'brak' && !d.items.length && (
        <Text size="xs" c="dimmed">
          Serwisant doda pozycje z cennika serwisu w protokole na telefonie; klient zapłaci kodem QR albo linkiem e-mailem. Fakturę po wpłacie wystawi CRM.
        </Text>
      )}
      {d.totalGr > 0 && !d.payOnline && (
        <Text size="xs" c="orange.8">
          Firma rozliczająca nie ma włączonych płatności online (ING Pay) — klient zapłaci przelewem, a fakturę trzeba wystawić ręcznie.
        </Text>
      )}
      {d.linkSentAt && (
        <Text size="xs" c="dimmed">
          Link do zapłaty wysłany {formatDateTime(d.linkSentAt)}
          {d.linkSentTo ? ` na ${d.linkSentTo}` : ''}
        </Text>
      )}
      {d.invoice.error && d.invoice.state !== 'wystawiona' && (
        <Text size="xs" c="red.7">
          {d.invoice.error}
        </Text>
      )}
      {d.overpayments.map((o) => (
        <Alert key={o.id} color={o.resolvedAt ? 'gray' : 'orange'} icon={<IconReceiptRefund />} p="xs" title={o.resolvedAt ? 'Nadpłata online zwrócona' : 'Nadpłata z płatności online — do zwrotu'}>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="xs">
              <Money gr={o.amountGr} /> · transakcja ING Pay {o.transactionId} · {formatDateTime(o.createdAt)}
              {o.resolvedAt ? ` · zwrot oznaczony ${formatDateTime(o.resolvedAt)}${o.resolvedByName ? ` przez ${o.resolvedByName}` : ''}${o.note ? ` · ${o.note}` : ''}` : ' · zwróć kwotę w panelu ING Pay, potem oznacz zwrot'}
            </Text>
            {!o.resolvedAt && d.can.resolveOverpayment && (
              <Button size="xs" variant="default" onClick={() => setRefund({ id: o.id, amountGr: o.amountGr, note: '' })}>
                Zwrócono
              </Button>
            )}
          </Group>
        </Alert>
      ))}
      {edit && <ChargeEditor lines={edit} warranty={warranty} saving={save.isPending} onCancel={() => setEdit(null)} onSave={(items) => save.mutate(items)} />}
      <Modal opened={!!refund} onClose={() => setRefund(null)} title="Zwrot nadpłaty online">
        {refund && (
          <Stack>
            <Text size="sm">
              Potwierdź, że zwróciłeś klientowi <Money gr={refund.amountGr} /> w panelu ING Pay.
            </Text>
            <Textarea label="Notatka (opcjonalnie)" value={refund.note} onChange={(e) => setRefund({ ...refund, note: e.currentTarget.value })} maxLength={500} autosize minRows={2} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setRefund(null)}>
                Anuluj
              </Button>
              <Button onClick={() => resolve.mutate(refund)} loading={resolve.isPending}>
                Oznacz zwrot
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/** Pozycje ustalone w biurze (np. przegląd z cennika przed wizytą). Ceny brutto. */
function ChargeEditor({ lines, warranty, saving, onCancel, onSave }: { lines: ServiceChargeLine[]; warranty: boolean; saving: boolean; onCancel: () => void; onSave: (l: ServiceChargeLine[]) => void }) {
  const [rows, setRows] = useState<ServiceChargeLine[]>(lines);
  const prices = useQuery({ queryKey: ['service-price-list'], queryFn: () => api<ServicePriceListItem[]>('/service-price-list') });
  const list = prices.data ?? [];
  const set = (i: number, patch: Partial<ServiceChargeLine>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = (id: string | null) => {
    const p = list.find((x) => x.id === id);
    if (p) setRows([...rows, { catalogItemId: p.id, name: p.name, unit: p.unit, quantity: 1, unitGrossGr: p.unitGrossGr, vatRate: p.vatRate, free: warranty }]);
  };
  const total = chargeTotalGr(rows);
  const invalid = rows.some((r) => r.name.trim().length < 2 || !(r.quantity > 0));
  const close = async () => {
    if (JSON.stringify(rows) === JSON.stringify(lines) || (await ask('Zamknąć bez zapisania zmian?', { title: 'Niezapisane zmiany', confirm: 'Zamknij' }))) onCancel();
  };
  return (
    <Modal opened onClose={() => void close()} title="Pozycje do zapłaty" size="lg">
      <Stack>
        {warranty && (
          <Alert color="blue" p="xs">
            Zgłoszenie gwarancyjne — pozycje są „w ramach gwarancji” (0 zł). Płatne pozycje dopisze serwisant w protokole z powodem albo zmień rozliczenie na „Płatna”.
          </Alert>
        )}
        {rows.map((r, i) => (
          <Group key={i} gap="xs" align="flex-end" wrap="wrap">
            <TextInput label={i === 0 ? 'Pozycja' : undefined} value={r.name} onChange={(e) => set(i, { name: e.currentTarget.value, catalogItemId: r.catalogItemId && e.currentTarget.value !== list.find((p) => p.id === r.catalogItemId)?.name ? null : r.catalogItemId })} w={{ base: '100%', sm: 220 }} />
            <NumberInput label={i === 0 ? 'Ilość' : undefined} value={r.quantity} min={0.001} decimalScale={3} decimalSeparator="," onChange={(v) => set(i, { quantity: Number(v) || 0 })} w={90} />
            <MoneyInput label={i === 0 ? 'Cena brutto' : undefined} valueGr={r.unitGrossGr} onChangeGr={(gr) => set(i, { unitGrossGr: gr ?? 0 })} w={120} disabled={r.free} />
            <Select label={i === 0 ? 'VAT' : undefined} data={['23', '8', '5', '0']} value={String(r.vatRate)} onChange={(v) => set(i, { vatRate: Number(v ?? 23) })} w={80} allowDeselect={false} />
            {warranty && <Checkbox label="W ramach gwarancji" checked={!!r.free} onChange={(e) => set(i, { free: e.currentTarget.checked })} mb={8} />}
            <ActionIcon variant="subtle" color="red" mb={4} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Usuń pozycję">
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Group gap="xs" wrap="wrap">
          <Select placeholder="Dodaj z cennika serwisu…" data={list.map((p) => ({ value: p.id, label: `${p.name} — ${(p.unitGrossGr / 100).toFixed(2).replace('.', ',')} zł` }))} value={null} onChange={add} searchable w={{ base: '100%', sm: 320 }} nothingFoundMessage="Brak pozycji — oznacz usługi w katalogu jako „Cennik serwisu”" />
          <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={() => setRows([...rows, { catalogItemId: null, name: '', unit: 'usł.', quantity: 1, unitGrossGr: 0, vatRate: 23, free: false }])}>
            Dodaj spoza cennika
          </Button>
        </Group>
        <Text size="sm">
          Razem do zapłaty: <Money gr={total} /> brutto
        </Text>
        {warranty && total > 0 && (
          <Text size="xs" c="red.7">
            Przy zgłoszeniu gwarancyjnym zapis płatnych pozycji wymaga zmiany rozliczenia na „Płatna”.
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => void close()}>
            Anuluj
          </Button>
          <Button onClick={() => onSave(rows.map((r) => ({ ...r, name: r.name.trim() })))} loading={saving} disabled={invalid || (warranty && total > 0)}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
